import { gql } from "@apollo/client";
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Select,
} from "@chakra-ui/react";
import { Field, useFormikContext } from "formik";
import { InputControl } from "formik-chakra-ui";
import _ from "lodash";
import React, { useCallback } from "react";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export function Form() {
  const formik = useFormikContext<FormikType>();
  const timezoneNames = React.useMemo(() => {
    return (Intl as any).supportedValuesOf("timeZone");
  }, []);

  return (
    <>
      <Heading as="h2" variant="admin">
        Basic information
      </Heading>
      <Flex direction="column">
        <InputControl
          name="name"
          label="Organization name"
          isRequired
          isDisabled
        >
          <Input size="sm" placeholder="Organization name" />
        </InputControl>
      </Flex>
      <Flex direction="column">
        <InputControl name="path" label="Path" isRequired isDisabled>
          <Input size="sm" placeholder="Path" />
        </InputControl>
      </Flex>
      <Flex direction="column">
        <FormControl
          isRequired
          isInvalid={formik.errors.timezoneName !== undefined}
        >
          <FormLabel htmlFor="timezoneName">Timezone name</FormLabel>
          <Field
            as={Select}
            id="timezoneName"
            name="timezoneName"
            placeholder="Select timezone"
          >
            {timezoneNames.map((value: string) => (
              <option value={value} key={value}>
                {value}
              </option>
            ))}
          </Field>
          <FormErrorMessage>{formik.errors.timezoneName}</FormErrorMessage>
        </FormControl>
      </Flex>
    </>
  );
}

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const path = useOrganizationPath();
  const { remoteData } = useRemoteDataQuery<
    GQL.GetOrganizationBasicInformation,
    GQL.GetOrganizationBasicInformationVariables
  >(
    gql`
      query GetOrganizationBasicInformation($path: String!) {
        organization(where: { path: { _eq: $path } }) {
          name
          path
          timezone_name
        }
      }
    `,
    {
      variables: {
        path: path ?? "",
      },
      skip: path === undefined,
    }
  );

  const [update] = useRemoteDataMutation<
    GQL.UpdateOrganizationBasicInformation,
    GQL.UpdateOrganizationBasicInformationVariables
  >(gql`
    mutation UpdateOrganizationBasicInformation(
      $path: String!
      $timeZone: String!
    ) {
      update_organization(
        where: { path: { _eq: $path } }
        _set: { timezone_name: $timeZone }
      ) {
        affected_rows
      }
    }
  `);

  const initialValues = React.useMemo(
    () =>
      remoteData
        .mapError<Error>(_.identity)
        .andThen((data): RD.RemoteData<Error, FormikType> => {
          if (data.organization.length > 1) {
            return RD.failure(
              new Error(`Found more than 1 organization with path: ${path}`)
            );
          }
          const organization = data.organization[0];
          if (!organization) {
            return RD.failure(
              new Error(`Organization with path ${path} not found!`)
            );
          }
          return RD.success({
            path: organization.path,
            name: organization.name,
            timezoneName: organization.timezone_name,
          });
        }),
    [path, remoteData]
  );

  const submit = useCallback(
    async (values: FormikType) => {
      if (!path) {
        throw new Error("Missing organization path");
      }

      const parsedValues = FormikSchema.parse(values);
      if (_.isEqual(parsedValues, initialValues.toNullable())) {
        // no changes, do nothing
        return;
      }

      await update({
        variables: {
          path,
          timeZone: values.timezoneName,
        },
      });
    },
    [initialValues, path, update]
  );

  return { initialValues, submit };
}

export const FormikSchema = z.object({
  name: z.string().min(1, "This field is required"),
  path: z.string().min(1, "This field is required"),
  timezoneName: z.string().min(1, "This field is required"),
});

type FormikType = z.infer<typeof FormikSchema>;
